import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Spinner from '../layout/Spinner';
import DashboardActions from './DashboardActions';
//import Experience from './Experience';
import Education from './Education';
import { deleteAccount /* getCurrentProfile */ } from '../../actions/profile';
import { getCurrentArtist } from '../../actions/artist';
import { getCurrentHost } from '../../actions/host';
import { getCurrentAttendee, updateEventDiscoveryRadius } from '../../actions/attendee';
import { Grid, Typography, Tooltip, IconButton, Collapse, TextField } from '@mui/material';
import Button from '../layout/SvgButton';

import AutoAwesomeTwoToneIcon from '@mui/icons-material/AutoAwesomeTwoTone';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import DateRangeTwoToneIcon from '@mui/icons-material/DateRangeTwoTone';
import MenuBookTwoToneIcon from '@mui/icons-material/MenuBookTwoTone';
//import ChangeCircleTwoToneIcon from '@mui/icons-material/ChangeCircleTwoTone';
import CachedTwoToneIcon from '@mui/icons-material/CachedTwoTone';
import ThumbUpAltTwoToneIcon from '@mui/icons-material/ThumbUpAltTwoTone';
import ThumbDownAltTwoToneIcon from '@mui/icons-material/ThumbDownAltTwoTone';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';

import {
    StackDateforDisplay,
    changeHats,
    closeEventEditDrawer,
} from '../../actions/app';
import ArtistDashboardEventCard from '../events/ArtistDashboardEventCard';
import NearMeToHostEventCard from './NearMeToHostEventCard';
import AnimatedEventCardSlot from './AnimatedEventCardSlot';
import AddHostEvent from '../events/AddHostEvent';
import AddArtistEvent from '../events/AddArtistEvent';
import EditArtistEvent from '../events/EditArtistEvent';
import HostDashboardEventCard from '../events/HostDashboardEventCard';
import AttendeeDashboardEventCard from '../events/AttendeeDashboardEventCard';
import GoogleMapForHosts from '../layout/GoogleMapForHosts';
import HostAdminActiveFalse from '../hosts/HostAdminActiveFalse';
import PastArtistEvents from './PastArtistEvents';
import PastHostEvents from './PastHostEvents';
import CanceledHostEvents from './CanceledHostEvents';
import CanceledAttendeeEvents, {
    getInviteEventId,
    isCanceledInvite,
} from './CanceledAttendeeEvents';
import PastAttendeeEvents from './PastAttendeeEvents';
import EmailNotVerifiedNotice from '../auth/EmailNotVerifiedNotice';
import CanceledArtistEvents from './CanceledArtistEvents';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import neighborhoodSvgRaw from '../../img/The_Neighborhood.svg?raw';

import {
    getMyArtistEventsOffers,
    getEventsNearMeToHost,
    getDiscoverableEvents,
} from '../../actions/event';

const neighborhoodIconSvg = neighborhoodSvgRaw
    .replace(/<\?xml[^?]*\?>\s*/, '')
    .replace(/<defs>[\s\S]*?<\/defs>/, '')
    .replace(
        '<svg id="a"',
        '<svg aria-hidden="true" focusable="false" width="24" height="24"'
    )
    .replaceAll(' class="b"', ' fill="currentColor"');

function NeighborhoodIcon() {
    return (
        <span
            style={{ display: 'inline-flex', lineHeight: 0 }}
            dangerouslySetInnerHTML={{ __html: neighborhoodIconSvg }}
        />
    );
}

// A custom hook that builds on useLocation to parse the query string for you.
function useQuery() {
    return new URLSearchParams(useLocation().search);
}

function normalizeDashboardRoles(user) {
    const r = user?.role;
    if (Array.isArray(r)) return r;
    if (typeof r === 'string' && r.trim()) return [r.trim()];
    return [];
}

function dashboardHatsFromRoles(roles) {
    return roles.filter((role) =>
        ['ARTIST', 'HOST', 'ATTENDEE'].includes(role)
    );
}

/** When several hats exist and profileHat is empty or invalid, prefer ARTIST, then HOST, then ATTENDEE. */
function pickDefaultProfileHat(hats) {
    if (!hats || hats.length === 0) return '';
    if (hats.length === 1) return hats[0];
    return (
        ['ARTIST', 'HOST', 'ATTENDEE'].find((h) => hats.includes(h)) || hats[0]
    );
}

const Dashboard = ({
    //getCurrentProfile,
    changeHats,
    deleteAccount,
    auth: { user, loading },
    //profile: { profile, loading },
    event: { myHostEvents, myArtistEvents, nearMeToHost, discoverableEvents },
    getCurrentArtist,
    artist,
    getCurrentHost,
    host,
    attendee,
    app,
    getEventsNearMeToHost,
    getMyArtistEventsOffers,
    getCurrentAttendee,
    getDiscoverableEvents,
    updateEventDiscoveryRadius,
    closeEventEditDrawer,
}) => {
    // Toggle to hide declined nearMeToHost events
    const [hideDeclinedNearMe, setHideDeclinedNearMe] = useState(false);
    const [discoveryRadiusDraft, setDiscoveryRadiusDraft] = useState(40);

    // Events the current host has just offered to host in this session. Used
    // to animate the card out of the "near me" section and into the
    // "you've offered to host" section once redux state catches up.
    const [recentlyOfferedIds, setRecentlyOfferedIds] = useState([]);

    // Events that have just been canceled in this session. Used to animate
    // the card out of the "you've offered to host" section and into the
    // "canceled events" section once redux state catches up.
    const [recentlyCanceledIds, setRecentlyCanceledIds] = useState([]);

    const handleHostOfferStarted = (eventId) => {
        if (!eventId) return;
        setRecentlyOfferedIds((prev) =>
            prev.includes(eventId) ? prev : [...prev, eventId]
        );
    };

    const handleCancelStarted = (eventId) => {
        if (!eventId) return;
        setRecentlyCanceledIds((prev) =>
            prev.includes(eventId) ? prev : [...prev, eventId]
        );
    };

    // After the card finishes expanding into its new section, set
    // `eventEditDrawer` to this event's id. That triggers the same animated
    // SVG perimeter outline (.eventCardSvgBorder .borderEffect in App.css)
    // used to highlight a recently-edited card.
    const handleAnimateInComplete = (eventId) => {
        if (!eventId) return;
        if (typeof closeEventEditDrawer === 'function') {
            closeEventEditDrawer(eventId);
        }
        // Remove this event from the tracking arrays so that re-mounts
        // (e.g. switching hats and switching back) don't replay the entrance
        // animation or auto-scroll to this card again.
        setRecentlyOfferedIds((prev) => prev.filter((id) => id !== eventId));
        setRecentlyCanceledIds((prev) => prev.filter((id) => id !== eventId));
    };

    const query = useQuery();
    const location = useLocation();
    const queryHat = query.get('hat');
    const queryEventID = query.get('eventID');
    const handledNotificationNav = useRef('');

    // useEffect(() => {
    //     getCurrentProfile();
    // }, [getCurrentProfile]);

    // moved the following to NearMeToHostEventCard.js
    // let query = useQuery();
    // const eventID = query.get('eventID');
    // let elementToScrollTo = document.getElementById(eventID);
    // useEffect(() => {
    //     elementToScrollTo = document.getElementById(eventID);
    // }, [eventID]);
    // useEffect(() => {
    //     if (eventID && elementToScrollTo) {
    //         elementToScrollTo.scrollIntoView({
    //             behavior: 'smooth',
    //             block: 'center',
    //         });
    //     }
    // }, [elementToScrollTo]);

    const confirmedMy = (thisEvent) =>
        thisEvent.createdBy !== 'HOST' &&
        thisEvent.confirmedHost &&
        host.me &&
        host.me._id &&
        thisEvent.confirmedHost === host.me._id
            ? true
            : false;

    const iDeclined = (thisEvent) =>
        artist.me &&
        (!thisEvent.declinedArtists ||
            thisEvent.declinedArtists.filter((declinedArtist) => {
                return declinedArtist.artist === artist.me._id;
            }).length <= 0)
            ? false
            : true;
    const iConfirmed = (thisEvent) => {
        // console.log(
        //     thisEvent.bookingWhen,
        //     'iConfirmed = ',
        //     artist.me &&
        //         thisEvent.confirmedArtist &&
        //         thisEvent.confirmedArtist === artist.me._id
        //         ? true
        //         : false
        // );
        return (artist &&
            artist.me &&
            thisEvent &&
            thisEvent.confirmedArtist &&
            thisEvent.confirmedArtist === artist.me._id) ||
            (artist &&
                artist.me &&
                thisEvent &&
                thisEvent.artist &&
                thisEvent.artist === artist.me._id)
            ? true
            : false;
    };

    useEffect(() => {
        const roles = user ? normalizeDashboardRoles(user) : [];
        if (!user || !roles.includes('ATTENDEE')) return;
        const hats = dashboardHatsFromRoles(roles);
        const hat =
            app.profileHat && hats.includes(app.profileHat)
                ? app.profileHat
                : pickDefaultProfileHat(hats);
        if (hat === 'ATTENDEE') {
            getCurrentAttendee();
            getDiscoverableEvents();
        }
    }, [user, app.profileHat, getCurrentAttendee, getDiscoverableEvents]);

    useEffect(() => {
        const savedRadius = Number(attendee?.me?.eventDiscoveryRadius);
        if (Number.isFinite(savedRadius) && savedRadius > 0) {
            setDiscoveryRadiusDraft(savedRadius);
        }
    }, [attendee?.me?.eventDiscoveryRadius]);
    useEffect(() => {
        const roles = user ? normalizeDashboardRoles(user) : [];
        if (user && roles.includes('ARTIST')) {
            getCurrentArtist();
            getMyArtistEventsOffers();
        }
    }, [getCurrentArtist, user]);
    useEffect(() => {
        const roles = user ? normalizeDashboardRoles(user) : [];
        if (user && roles.includes('HOST')) {
            getCurrentHost();
            getEventsNearMeToHost();
        }
    }, [getCurrentHost, user]);

    // Keep Redux profileHat aligned with roles: attendee-only users always get ATTENDEE;
    // clear invalid hats (e.g. HOST hat left over after role change).
    // Notification deep-links request a hat via ?hat= (and navigate state). Apply
    // that once per navigation so a later manual hat switch is not overwritten.
    useEffect(() => {
        if (!user) return;
        const hats = dashboardHatsFromRoles(normalizeDashboardRoles(user));
        if (hats.length === 0) return;

        const current = app.profileHat;
        const currentValid = Boolean(current && hats.includes(current));
        const queryHatValid = Boolean(queryHat && hats.includes(queryHat));
        const navStamp =
            (location.state &&
                (location.state.openedAt || location.state.fromNotification)) ||
            location.search ||
            '';

        if (queryHatValid && handledNotificationNav.current !== String(navStamp)) {
            handledNotificationNav.current = String(navStamp);
            if (current !== queryHat) {
                changeHats(queryHat);
            }
            return;
        }

        if (hats.length === 1) {
            if (current !== hats[0]) {
                changeHats(hats[0]);
            }
            return;
        }

        if (!currentValid) {
            changeHats(pickDefaultProfileHat(hats));
        }
    }, [user, app.profileHat, changeHats, queryHat, location.state, location.search]);

    const dayBeforeYesterday = new Date();
    dayBeforeYesterday.setDate(dayBeforeYesterday.getDate() - 2);
    // console.log('dayBeforeYesterday', dayBeforeYesterday);

    const isFuture = (bookingWhen) => {
        if (new Date(bookingWhen) >= dayBeforeYesterday) {
            // console.log(
            //     'new Date(bookingWhen) is greater than dayBeforeYesterday',
            //     new Date(bookingWhen),
            //     'dayBeforeYesterday',
            //     dayBeforeYesterday
            // );
            return true;
        } else {
            // console.log(
            //     'new Date(bookingWhen) is less than dayBeforeYesterday',
            //     new Date(bookingWhen),
            //     'dayBeforeYesterday',
            //     dayBeforeYesterday
            // );
            return false;
        }
    };

    const isEmailVerified = () => {
        if (!user) return false;
        const v = user.verifiedEmail;
        if (!v) return false;
        const t = new Date(v).getTime();
        return !isNaN(t);
    };

    // Derive filtered nearMeToHost list based on toggle and whether current host declined
    const hostId = host && host.me && host.me._id;
    const roles = normalizeDashboardRoles(user);
    const hatsAvailable = dashboardHatsFromRoles(roles);
    const activeHat =
        app.profileHat && hatsAvailable.includes(app.profileHat)
            ? app.profileHat
            : pickDefaultProfileHat(hatsAvailable);
    const invitedEvents = Array.isArray(attendee?.me?.invitedEvents)
        ? attendee.me.invitedEvents
        : [];
    const getInviteBookingWhen = (inv) =>
        inv?.event && typeof inv.event === 'object'
            ? inv.event.bookingWhen
            : null;
    const sortBySoonest = (aWhen, bWhen) => {
        const aTime = new Date(aWhen || 0).getTime();
        const bTime = new Date(bWhen || 0).getTime();
        return aTime - bTime;
    };
    const nonCanceledInvitedEvents = invitedEvents.filter(
        (inv) => inv?.event && !isCanceledInvite(inv)
    );
    const upcomingInvitedEvents = nonCanceledInvitedEvents
        .filter((inv) => {
            const when = getInviteBookingWhen(inv);
            return !when || isFuture(when);
        })
        .sort((a, b) =>
            sortBySoonest(getInviteBookingWhen(a), getInviteBookingWhen(b))
        );
    const invitedEventIds = new Set(
        nonCanceledInvitedEvents
            .map((inv) => getInviteEventId(inv))
            .filter(Boolean)
            .map((id) => String(id))
    );
    const nearbyConcerts = Array.isArray(discoverableEvents)
        ? discoverableEvents
        : [];
    const verifiedDiscoverConcerts = nearbyConcerts
        .filter((concert) => {
            const id = concert && concert._id ? String(concert._id) : '';
            return (
                concert?.attendanceVisibility === 'VERIFIED' &&
                id &&
                !invitedEventIds.has(id)
            );
        })
        .sort((a, b) => sortBySoonest(a?.bookingWhen, b?.bookingWhen));
    const publicDiscoverConcerts = nearbyConcerts
        .filter((concert) => {
            const id = concert && concert._id ? String(concert._id) : '';
            return (
                concert?.attendanceVisibility === 'PUBLIC' &&
                id &&
                !invitedEventIds.has(id)
            );
        })
        .sort((a, b) => sortBySoonest(a?.bookingWhen, b?.bookingWhen));
    const attendeeHasLocation =
        attendee?.me?.latLong &&
        Array.isArray(attendee.me.latLong.coordinates) &&
        attendee.me.latLong.coordinates.length === 2 &&
        !(
            Number(attendee.me.latLong.coordinates[0]) === 0 &&
            Number(attendee.me.latLong.coordinates[1]) === 0
        );
    const concertMapMarkers = nearbyConcerts
        .filter((concert) => {
            const coords = concert?.mapLocation?.coordinates;
            return (
                concert.status !== 'CANCELED' &&
                Array.isArray(coords) &&
                coords.length === 2 &&
                Number.isFinite(Number(coords[0])) &&
                Number.isFinite(Number(coords[1])) &&
                !(Number(coords[0]) === 0 && Number(coords[1]) === 0)
            );
        })
        .map((concert) => {
            const artist = concert.confirmedArtist || concert.artist || {};
            return {
                _id: concert._id,
                slug: artist.slug || String(concert._id),
                stageName: artist.stageName,
                squareImg: artist.squareImg,
                city: concert.bookingWhere?.city,
                state: concert.bookingWhere?.state,
                zipCode: concert.bookingWhere?.zipCode,
                bookingWhen: concert.bookingWhen,
                latLong: concert.mapLocation,
            };
        });
    const nearMeToHostFiltered = Array.isArray(nearMeToHost)
        ? nearMeToHost.filter((evt) => {
              if (!hideDeclinedNearMe || !hostId) return true;
              if (!evt || !evt.declinedHosts || evt.declinedHosts.length === 0)
                  return true;
              return evt.declinedHosts.every(
                  (d) => !d || !d.host || d.host !== hostId
              );
          })
        : [];

    const isDeclinedByCurrentHost = (evt) => {
        if (!hostId) return false;
        if (!evt || !evt.declinedHosts || evt.declinedHosts.length === 0)
            return false;
        return evt.declinedHosts.some((d) => d && d.host && d.host === hostId);
    };

    // After the matching hat is showing, scroll the notification's event into view.
    // Retry until the card exists and is actually visible (past-events collapse, etc.).
    useEffect(() => {
        if (!queryEventID) return;
        if (queryHat && activeHat !== queryHat) return;

        let cancelled = false;
        let attempts = 0;
        const maxAttempts = 30;

        const tryScroll = () => {
            if (cancelled) return true;
            const el = document.getElementById(String(queryEventID));
            if (!el) return false;
            const height = el.getBoundingClientRect().height;
            if (height < 8) return false;
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return true;
        };

        if (tryScroll()) {
            return () => {
                cancelled = true;
            };
        }

        const interval = setInterval(() => {
            attempts += 1;
            if (tryScroll() || attempts >= maxAttempts) {
                clearInterval(interval);
            }
        }, 120);

        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [
        queryEventID,
        queryHat,
        activeHat,
        location.key,
        location.state,
        nearMeToHost,
        invitedEvents,
        myHostEvents,
        myArtistEvents,
    ]);

    // Helper function to check if current host has made an offer for this event
    const hasHostOffered = (thisEvent) => {
        return (
            host.me &&
            host.me._id &&
            thisEvent.offersFromHosts &&
            thisEvent.offersFromHosts.some(
                (offer) =>
                    offer.host &&
                    (typeof offer.host === 'string'
                        ? offer.host
                        : offer.host._id) === host.me._id
            )
        );
    };

    // Helper function to determine if an event should be shown in host dashboard
    const shouldShowHostEvent = (myEvent) => {
        return (
            // Show if host is confirmed for the event
            // OR show if event is pending and host has made an offer
            ((myEvent.confirmedHost &&
                (myEvent.status === 'CONFIRMED' ||
                    myEvent.status === 'PENDING')) ||
                (myEvent.status === 'PENDING' && hasHostOffered(myEvent))) &&
            isFuture(myEvent.bookingWhen)
        );
    };

    if (loading && user === null) {
        return <Spinner />;
    }

    if (user && !isEmailVerified()) {
        return (
            <Fragment>
                <Grid
                    container
                    justifyContent="center"
                    alignItems="start"
                    sx={{
                        minHeight: '80vh',
                        padding: '20px!important',
                        width: '100vw',
                        maxWidth: '100vw',
                        margin: '0 auto',
                        flexDirection: { xs: 'column', md: 'row' },
                    }}
                >
                    <Grid
                        item
                        container
                        direction="column"
                        className="middle"
                        size={{ xs: 12, md: 6 }}
                        sx={{
                            padding: { xs: '0', md: '0 20px' },
                            width: { xs: '100%', md: '75%' },
                            maxWidth: { md: '750px' },
                        }}
                    >
                        <EmailNotVerifiedNotice />
                    </Grid>
                </Grid>
            </Fragment>
        );
    }

    return (
        <Fragment>
            <Grid
                container
                justifyContent="center"
                alignItems="start"
                // direction="row"
                sx={{
                    minHeight: '80vh',
                    padding: '20px!important',
                    width: '100vw',
                    maxWidth: '100vw',
                    margin: '0 auto',
                    flexDirection: { xs: 'column', md: 'row' },
                }}
            >
                <Grid
                    item
                    container
                    direction="column"
                    className="leftSide"
                    size={{ xs: 12, md: 3 }}
                    sx={{
                        flex: 1,
                        maxWidth: { md: '25%', xs: '100%' },
                    }}
                >
                    <Grid
                        item
                        container
                        direction="column"
                        className="welcomeSection"
                    >
                        <Grid item textAlign="center">
                            <Typography component="h2">
                                Welcome to your Porchlight Dashboard
                                {user && user.name
                                    ? ', ' + user.name.split(' ')[0] + '!'
                                    : '!'}
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            sx={{
                                margin: '8px auto',
                            }}
                        >
                            <p className="welcomeNote">
                                {activeHat === 'ARTIST'
                                    ? 'We’re excited to connect you to our growing network of amazing hosts. Make sure you check out the musician guide (below). Thanks for joining us in this mission to  curate profound experiences and relationships around the true and beautiful.'
                                    : activeHat === 'HOST'
                                      ? 'We’re honored to serve you as you host profound experiences and develop relationships around the true and beautiful.'
                                      : 'For now, we’ll notify you about once a month about upcoming Porchlight events and opportunities. Soon, you’ll be able to view upcoming local Porchlight events right on your dashboard. If you ever decide you’d like to host a Porchlight event, just sign up to host below!'}
                            </p>
                        </Grid>

                        {/* CALENDLY STUFF
      <Fragment>
          <DashboardActions></DashboardActions>
      </Fragment> */}
                        {/* {profile !== null ? (
				<Fragment>
					<Experience experience={profile.experience}></Experience>
					<Education education={profile.education}></Education>
					<div className='my-2'>
						<button className='btn btn-danger' onClick={() => deleteAccount()}>
							<i className='fas fa-user-minus'>Delete my account</i>
						</button>
					</div>
				</Fragment>
			) : (
				<Fragment>
					<p> You have not yet setup a profile, please add some info</p>
					<Link to='/create-profile' className='btn btn-primary my-1'>
						Create Profile
					</Link>
				</Fragment>
			)} */}
                    </Grid>
                    {hatsAvailable.length > 1 && (
                        <Grid item container className="changeProfileHat">
                            <Typography component={'p'}>
                                It looks like you have multiple Porchlight hats.
                                You’re currently viewing your{' '}
                                <span
                                    style={{
                                        fontWeight: 'bold',
                                        color: 'var(--light-color)',
                                    }}
                                >
                                    {activeHat}
                                </span>{' '}
                                stuff. Switch hats to see your other stuff.
                            </Typography>
                            <Grid
                                item
                                container
                                spacing={1}
                                justifyContent="center"
                                sx={{
                                    margin: '8px auto 8px',
                                }}
                            >
                                {hatsAvailable
                                    .filter((hat) => hat !== activeHat)
                                    .map((hat) => (
                                        <Grid item key={hat}>
                                            <Button
                                                btnwidth="250"
                                                onClick={() => changeHats(hat)}
                                            >
                                                <CachedTwoToneIcon />
                                                Put on my {hat} hat
                                            </Button>
                                        </Grid>
                                    ))}
                            </Grid>
                        </Grid>
                    )}
                    {activeHat === 'ARTIST' &&
                    user &&
                    roles.includes('ARTIST') ? (
                        <Grid
                            item
                            container
                            className="artistStuff"
                            size={{ xs: 12 }}
                        >
                            {artist.me && artist.me._id ? (
                                [
                                    <Grid
                                        item
                                        sx={{
                                            margin: '8px auto',
                                        }}
                                    >
                                        <a
                                            target="_blank"
                                            href="https://docs.google.com/document/d/1skxIQjIhEOs07k06ymmss1lMO-Q9Q4j8kI68Vc0u5hE/edit?usp=sharing"
                                        >
                                            <Button btnwidth="250" className="">
                                                <MenuBookTwoToneIcon /> Musician
                                                Guide
                                            </Button>
                                        </a>
                                    </Grid>,

                                    <Grid
                                        className="artistToolkit"
                                        item
                                        sx={{
                                            margin: '8px auto',
                                        }}
                                    >
                                        <p> </p>
                                        <a
                                            target="_blank"
                                            href="https://drive.google.com/drive/u/0/folders/1Zl_BAAvia0G9xyyVKGPTadq08kNs3l9R"
                                        >
                                            <Button btnwidth="250" className="">
                                                <HomeRepairServiceIcon />
                                                Artist Toolkit
                                            </Button>
                                        </a>
                                    </Grid>,
                                    <Grid
                                        key="neighborhood"
                                        item
                                        sx={{
                                            margin: '8px auto',
                                        }}
                                    >
                                        <a
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            href="https://theneighborhood.digital/share/kJMmEOBZ5v14MMQ6?utm_source=manual"
                                        >
                                            <Button btnwidth="250" className="">
                                                <NeighborhoodIcon />
                                                The Neighborhood
                                            </Button>
                                        </a>
                                    </Grid>,
                                    <Grid
                                        item
                                        sx={{
                                            margin: '8px auto',
                                        }}
                                    >
                                        <Link to="/edit-artist-profile">
                                            <Button btnwidth="250" className="">
                                                <EditTwoToneIcon /> Edit My
                                                Artist Profile
                                            </Button>
                                        </Link>
                                    </Grid>,
                                ]
                            ) : (
                                <Fragment>
                                    <Grid
                                        item
                                        sx={{
                                            margin: '8px auto',
                                        }}
                                    >
                                        <p className="">
                                            Please make sure the information in
                                            your profile is always accurate and
                                            up-to-date. Your answers help us
                                            connect you with folks who want to
                                            host concerts in their spaces.
                                        </p>
                                    </Grid>

                                    <Grid
                                        item
                                        sx={{
                                            margin: '8px auto',
                                        }}
                                    >
                                        <p> </p>
                                        <Link to="/edit-artist-profile">
                                            <Button btnwidth="250" className="">
                                                <AutoAwesomeTwoToneIcon></AutoAwesomeTwoToneIcon>
                                                Create My Profile
                                            </Button>
                                        </Link>
                                    </Grid>
                                </Fragment>
                            )}
                            {/* {(artist.me &&
                                artist.me._id &&
                                artist.me.active &&
                                artist.me.bookingWhen.length > 0) ||
                            (Array.isArray(user.role) &&
                                user.role.indexOf('ARTIST') != -1 &&
                                user.role.indexOf('ADMIN') != -1 &&
                                artist.me &&
                                artist.me._id) ? (
                                <Grid
                                    item
                                    sx={{
                                        margin: '8px auto',
                                    }}
                                >
                                    <Link to="/edit-artist-booking">
                                        <Button btnwidth="250" className="">
                                            <DateRangeTwoToneIcon /> Edit My
                                            Booking Info
                                        </Button>
                                    </Link>
                                </Grid>
                            ) : artist.me &&
                              artist.me._id &&
                              artist.me.active ? (
                                <Grid
                                    item
                                    sx={{
                                        margin: '8px auto',
                                    }}
                                >
                                    <p> </p>
                                    <Link to="/edit-artist-booking">
                                        <Button btnwidth="250" className="">
                                            Start Booking Shows
                                        </Button>
                                    </Link>
                                </Grid>
                            ) : (
                                ''
                            )} */}
                        </Grid>
                    ) : (
                        <></>
                    )}
                    {activeHat === 'ATTENDEE' &&
                        user &&
                        user.role &&
                        user.role.indexOf('ATTENDEE') != -1 && (
                            <Grid item container className="attendeeStuff">
                                {user.role.indexOf('HOST') === -1 && (
                                    <Grid
                                        item
                                        sx={{
                                            margin: '8px auto',
                                        }}
                                    >
                                        <p> </p>
                                        <Link to="/edit-host-profile">
                                            <Button btnwidth="250" className="">
                                                <AutoAwesomeTwoToneIcon></AutoAwesomeTwoToneIcon>
                                                Sign Up to Host
                                            </Button>
                                        </Link>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    {activeHat === 'HOST' &&
                        user &&
                        user.role &&
                        user.role.indexOf('HOST') != -1 && (
                            <>
                                {' '}
                                {/* <Grid
                                    item
                                    container
                                    className="newsletterArchive"
                                >
                                    <Grid
                                        item
                                        sx={{
                                            margin: '8px auto',
                                        }}
                                    >
                                        <p> </p>
                                        <a
                                            target="_blank"
                                            href="https://us8.campaign-archive.com/home/?u=77c32f3115181cdf1e389d367&id=57439ec968"
                                        >
                                            <Button btnwidth="250" className="">
                                                <NewspaperIcon />
                                                Host Newsletter Archive
                                            </Button>
                                        </a>
                                    </Grid>
                                </Grid> */}
                                <Grid
                                    item
                                    sx={{
                                        margin: '8px auto',
                                    }}
                                    className="hostGuideBtn"
                                >
                                    <a
                                        target="_blank"
                                        href="https://docs.google.com/document/d/10jINNrRtF1UCXpXnNS21b2riUUJAfnTBDfPG61bZSsk/edit?usp=sharing"
                                    >
                                        <Button btnwidth="250">
                                            <MenuBookTwoToneIcon
                                                sx={{ marginRight: '5px' }}
                                            />{' '}
                                            The Hosting Guide
                                        </Button>
                                    </a>
                                </Grid>
                                <Grid item container className="hostToolkit">
                                    <Grid
                                        item
                                        sx={{
                                            margin: '8px auto',
                                        }}
                                    >
                                        <p> </p>
                                        <a
                                            target="_blank"
                                            href="https://drive.google.com/drive/u/0/folders/1WHzyTePAMnuJexj9_n7ht-vRo0AUA0vJ"
                                        >
                                            <Button btnwidth="250" className="">
                                                <HomeRepairServiceIcon />
                                                Host Toolkit
                                            </Button>
                                        </a>
                                    </Grid>
                                </Grid>{' '}
                                <Grid item container className="hostStuff">
                                    <Grid
                                        item
                                        sx={{
                                            margin: '8px auto',
                                        }}
                                    >
                                        <p> </p>
                                        <Link to="/edit-host-profile">
                                            <Button btnwidth="250" className="">
                                                <EditTwoToneIcon />
                                                Edit My Host Profile
                                            </Button>
                                        </Link>
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    {/* End welcomeSection */}
                </Grid>
                {/* End leftSide */}
                {activeHat === 'ATTENDEE' &&
                    user &&
                    roles.includes('ATTENDEE') && (
                        <Grid
                            item
                            container
                            direction="column"
                            className="middle"
                            size={{ xs: 12, md: 6 }}
                            sx={{
                                padding: { xs: '0', md: '0 20px' },
                                width: { xs: '100%', md: '75%' },
                                maxWidth: { md: '750px' },
                            }}
                        >
                            <Grid
                                item
                                container
                                className="attendeeStuff artistStuff"
                                size={{ xs: 12 }}
                                direction="column"
                            >
                                <Grid
                                    container
                                    direction="column"
                                    sx={{ marginBottom: '20px' }}
                                >
                                    <Grid item xs={12}>
                                        <Typography component="h2">
                                            Concerts near you
                                        </Typography>
                                    </Grid>
                                    {!attendeeHasLocation ? (
                                        <Typography
                                            sx={{ mt: 1, opacity: 0.85 }}
                                        >
                                            Add your city to your attendee
                                            profile so we can show concerts
                                            within a mileage radius.
                                        </Typography>
                                    ) : nearbyConcerts.length === 0 ? (
                                        <Typography
                                            sx={{ mt: 1, opacity: 0.85 }}
                                        >
                                            There’s nothing to show right now.
                                        </Typography>
                                    ) : (
                                        <Grid
                                            item
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                gap: 1,
                                                flexWrap: 'wrap',
                                                mt: 1,
                                            }}
                                        >
                                            <Typography component="span">
                                                Within a radius of
                                            </Typography>
                                            <TextField
                                                variant="standard"
                                                type="number"
                                                name="eventDiscoveryRadius"
                                                value={discoveryRadiusDraft}
                                                onChange={(e) =>
                                                    setDiscoveryRadiusDraft(
                                                        e.target.value
                                                    )
                                                }
                                                onBlur={async () => {
                                                    const miles = Math.min(
                                                        Math.max(
                                                            Number(
                                                                discoveryRadiusDraft
                                                            ) || 40,
                                                            1
                                                        ),
                                                        500
                                                    );
                                                    setDiscoveryRadiusDraft(
                                                        miles
                                                    );
                                                    if (
                                                        miles !==
                                                        Number(
                                                            attendee?.me
                                                                ?.eventDiscoveryRadius ||
                                                                40
                                                        )
                                                    ) {
                                                        await updateEventDiscoveryRadius(
                                                            miles
                                                        );
                                                        getDiscoverableEvents();
                                                    }
                                                }}
                                                inputProps={{
                                                    min: 1,
                                                    max: 500,
                                                    step: 1,
                                                }}
                                                sx={{ width: '72px' }}
                                            />
                                            <Typography component="span">
                                                miles
                                            </Typography>
                                        </Grid>
                                    )}
                                    {concertMapMarkers.length > 0 && (
                                        <GoogleMapForHosts
                                            markers={concertMapMarkers}
                                            radius={
                                                attendeeHasLocation
                                                    ? Number(
                                                          discoveryRadiusDraft
                                                      ) || 40
                                                    : undefined
                                            }
                                            circleCenter={
                                                attendeeHasLocation
                                                    ? attendee.me.latLong
                                                    : undefined
                                            }
                                            preferredArtists={[]}
                                            offsetOverlapping
                                            mapHeight="320px"
                                            zoom={
                                                attendeeHasLocation ? 7 : 4.25
                                            }
                                            onPinClick={(marker) => {
                                                if (!marker?._id) return;
                                                const el =
                                                    document.getElementById(
                                                        String(marker._id)
                                                    );
                                                if (el) {
                                                    el.scrollIntoView({
                                                        behavior: 'smooth',
                                                        block: 'center',
                                                    });
                                                }
                                            }}
                                        />
                                    )}
                                </Grid>
                                {upcomingInvitedEvents.length > 0 && (
                                    <Grid
                                        container
                                        direction="column"
                                        sx={{ marginBottom: '20px' }}
                                    >
                                        <Grid item xs={12}>
                                            <Typography component="h2">
                                                {upcomingInvitedEvents.length > 1
                                                    ? `You’ve been invited to these ${upcomingInvitedEvents.length} concerts`
                                                    : `You’ve been invited to this concert`}
                                                :
                                            </Typography>
                                        </Grid>
                                        {upcomingInvitedEvents.map(
                                            (inv) =>
                                                inv?.event && (
                                                    <AttendeeDashboardEventCard
                                                        key={
                                                            getInviteEventId(
                                                                inv
                                                            ) +
                                                            (inv.addedAt || '')
                                                        }
                                                        invite={inv}
                                                    />
                                                )
                                        )}
                                    </Grid>
                                )}
                                {verifiedDiscoverConcerts.length > 0 && (
                                    <Grid
                                        container
                                        direction="column"
                                        sx={{ marginBottom: '20px' }}
                                    >
                                        <Grid item xs={12}>
                                            <Typography component="h2">
                                                Open to verified attendees
                                            </Typography>
                                        </Grid>
                                        {verifiedDiscoverConcerts.map(
                                            (concert) => (
                                                <AttendeeDashboardEventCard
                                                    key={concert._id}
                                                    event={concert}
                                                    invite={{
                                                        event: concert._id,
                                                    }}
                                                    showInvitedChip
                                                />
                                            )
                                        )}
                                    </Grid>
                                )}
                                {publicDiscoverConcerts.length > 0 && (
                                    <Grid
                                        container
                                        direction="column"
                                        sx={{ marginBottom: '20px' }}
                                    >
                                        <Grid item xs={12}>
                                            <Typography component="h2">
                                                Public concerts nearby
                                            </Typography>
                                        </Grid>
                                        {publicDiscoverConcerts.map(
                                            (concert) => (
                                                <AttendeeDashboardEventCard
                                                    key={concert._id}
                                                    event={concert}
                                                    invite={{
                                                        event: concert._id,
                                                    }}
                                                    showInvitedChip={false}
                                                />
                                            )
                                        )}
                                    </Grid>
                                )}
                                <PastAttendeeEvents />
                                <CanceledAttendeeEvents />
                            </Grid>
                        </Grid>
                    )}
                {activeHat === 'ARTIST' &&
                    user &&
                    user.role &&
                    user.role.indexOf('ARTIST') != -1 &&
                    artist.me &&
                    artist.me._id &&
                    (artist.me.active ||
                        (Array.isArray(user.role) &&
                            user.role.indexOf('ARTIST') != -1 &&
                            user.role.indexOf('ADMIN') != -1)) && (
                        <Grid
                            item
                            container
                            direction="column"
                            className="middle"
                            size={{ xs: 12, md: 6 }}
                            sx={{
                                padding: { xs: '0', md: '0 20px' },
                                width: { xs: '100%', md: '75%' },
                                maxWidth: { md: '750px' },
                            }}
                        >
                            <Grid
                                item
                                container
                                className="artistStuff"
                                size={{ xs: 12 }}
                                direction="column"
                            >
                                {/* Offers to consider */}
                                {myArtistEvents &&
                                    myArtistEvents.length > 0 &&
                                    myArtistEvents.filter(
                                        (myEvent) =>
                                            (!myEvent.confirmedHost ||
                                                (myEvent.createdBy === 'HOST' &&
                                                    myEvent.status ===
                                                        'PENDING')) &&
                                            myEvent.offersFromHosts &&
                                            myEvent.offersFromHosts.length > 0
                                    ).length > 0 && (
                                        <Grid
                                            container
                                            direction="column"
                                            sx={{ marginBottom: '20px' }}
                                        >
                                            <Grid item>
                                                <Typography component="h2">
                                                    {myArtistEvents.filter(
                                                        (myEvent) =>
                                                            (!myEvent.confirmedHost ||
                                                                (myEvent.createdBy ===
                                                                    'HOST' &&
                                                                    myEvent.status ===
                                                                        'PENDING')) &&
                                                            myEvent.offersFromHosts &&
                                                            myEvent
                                                                .offersFromHosts
                                                                .length > 0
                                                    ).length > 1
                                                        ? `These ${
                                                              myArtistEvents.filter(
                                                                  (myEvent) =>
                                                                      (!myEvent.confirmedHost ||
                                                                          (myEvent.createdBy ===
                                                                              'HOST' &&
                                                                              myEvent.status ===
                                                                                  'PENDING')) &&
                                                                      myEvent.offersFromHosts &&
                                                                      myEvent
                                                                          .offersFromHosts
                                                                          .length >
                                                                          0
                                                              ).length
                                                          } concerts have booking offers for you to consider`
                                                        : `This concert has ${
                                                              myArtistEvents.filter(
                                                                  (myEvent) =>
                                                                      (!myEvent.confirmedHost ||
                                                                          (myEvent.createdBy ===
                                                                              'HOST' &&
                                                                              myEvent.status ===
                                                                                  'PENDING')) &&
                                                                      myEvent.offersFromHosts &&
                                                                      myEvent
                                                                          .offersFromHosts
                                                                          .length >
                                                                          0
                                                              )[0] &&
                                                              myArtistEvents.filter(
                                                                  (myEvent) =>
                                                                      (!myEvent.confirmedHost ||
                                                                          (myEvent.createdBy ===
                                                                              'HOST' &&
                                                                              myEvent.status ===
                                                                                  'PENDING')) &&
                                                                      myEvent.offersFromHosts &&
                                                                      myEvent
                                                                          .offersFromHosts
                                                                          .length >
                                                                          0
                                                              )[0]
                                                                  .offersFromHosts &&
                                                              myArtistEvents.filter(
                                                                  (myEvent) =>
                                                                      (!myEvent.confirmedHost ||
                                                                          (myEvent.createdBy ===
                                                                              'HOST' &&
                                                                              myEvent.status ===
                                                                                  'PENDING')) &&
                                                                      myEvent.offersFromHosts &&
                                                                      myEvent
                                                                          .offersFromHosts
                                                                          .length >
                                                                          0
                                                              )[0]
                                                                  .offersFromHosts
                                                                  .length > 1
                                                                  ? 'booking offers'
                                                                  : 'a booking offer'
                                                          } for you to consider`}
                                                    :
                                                </Typography>
                                            </Grid>
                                            {/* {bookingWhenWhere && bookingWhenWhere.length > 0 && bookingWhenWhere[0].when ? //check to be sure there's a valid first entry */}
                                            <Grid
                                                container
                                                className="whenBooking"
                                                direction="row"
                                                justifyContent="center"
                                                alignItems="center"
                                                spacing={2}
                                                sx={{
                                                    margin: '0px auto 16px',
                                                    width: '100%',
                                                }}
                                            >
                                                {myArtistEvents
                                                    .filter((myEvent) => {
                                                        return (
                                                            (!myEvent.confirmedHost ||
                                                                (myEvent.createdBy ===
                                                                    'HOST' &&
                                                                    myEvent.status ===
                                                                        'PENDING')) &&
                                                            myEvent.offersFromHosts &&
                                                            myEvent
                                                                .offersFromHosts
                                                                .length > 0
                                                        );
                                                    }) //.filter(e => e) to remove any null values
                                                    .map(
                                                        (thisEvent, idx) =>
                                                            thisEvent.bookingWhen &&
                                                            thisEvent.bookingWhere && (
                                                                <ArtistDashboardEventCard
                                                                    key={idx}
                                                                    thisEvent={
                                                                        thisEvent
                                                                    }
                                                                />
                                                            )
                                                    )}
                                            </Grid>
                                        </Grid>
                                    )}
                                {/* Confirmed Concerts */}
                                {myArtistEvents &&
                                    myArtistEvents.length > 0 &&
                                    myArtistEvents.filter(
                                        (myEvent) =>
                                            myEvent.confirmedHost &&
                                            myEvent.status === 'CONFIRMED' &&
                                            iConfirmed(myEvent) &&
                                            new Date(myEvent.bookingWhen) >
                                                dayBeforeYesterday
                                    ).length > 0 && (
                                        <Grid
                                            container
                                            direction="column"
                                            sx={{ marginBottom: '20px' }}
                                        >
                                            <Grid item>
                                                <Typography component="h2">
                                                    {myArtistEvents.filter(
                                                        (myEvent) =>
                                                            myEvent.confirmedHost &&
                                                            myEvent.status ===
                                                                'CONFIRMED' &&
                                                            iConfirmed(
                                                                myEvent
                                                            ) &&
                                                            new Date(
                                                                myEvent.bookingWhen
                                                            ) >
                                                                dayBeforeYesterday
                                                    ).length > 1
                                                        ? `You booked these ${
                                                              myArtistEvents.filter(
                                                                  (myEvent) =>
                                                                      myEvent.confirmedHost &&
                                                                      myEvent.status ===
                                                                          'CONFIRMED' &&
                                                                      iConfirmed(
                                                                          myEvent
                                                                      ) &&
                                                                      new Date(
                                                                          myEvent.bookingWhen
                                                                      ) >
                                                                          dayBeforeYesterday
                                                              ).length
                                                          } upcoming concerts`
                                                        : `You booked this upcoming concert`}
                                                    :
                                                </Typography>
                                            </Grid>
                                            {/* {bookingWhenWhere && bookingWhenWhere.length > 0 && bookingWhenWhere[0].when ? //check to be sure there's a valid first entry */}
                                            <Grid
                                                container
                                                className="whenBooking"
                                                direction="row"
                                                justifyContent="center"
                                                alignItems="center"
                                                spacing={2}
                                                sx={{
                                                    margin: '0px auto 16px',
                                                    width: '100%',
                                                }}
                                            ></Grid>
                                            {myArtistEvents
                                                .filter(
                                                    (myEvent) =>
                                                        myEvent.confirmedHost &&
                                                        myEvent.status ===
                                                            'CONFIRMED' &&
                                                        iConfirmed(myEvent) &&
                                                        new Date(
                                                            myEvent.bookingWhen
                                                        ) > dayBeforeYesterday
                                                )
                                                .map(
                                                    (thisEvent, idx) =>
                                                        thisEvent.bookingWhen &&
                                                        thisEvent.bookingWhere && (
                                                            <Collapse
                                                                key={
                                                                    (thisEvent &&
                                                                        thisEvent._id) ||
                                                                    idx
                                                                }
                                                                in={
                                                                    !recentlyCanceledIds.includes(
                                                                        thisEvent._id
                                                                    )
                                                                }
                                                                timeout={{
                                                                    enter: 300,
                                                                    exit: 350,
                                                                }}
                                                                unmountOnExit
                                                                sx={{
                                                                    width: '100%',
                                                                }}
                                                            >
                                                                <ArtistDashboardEventCard
                                                                    thisEvent={
                                                                        thisEvent
                                                                    }
                                                                    onCancelStarted={
                                                                        handleCancelStarted
                                                                    }
                                                                />
                                                            </Collapse>
                                                        )
                                                )}
                                        </Grid>
                                    )}
                                {/* Confirmed by other artist concerts */}
                                {myArtistEvents &&
                                    myArtistEvents.length > 0 &&
                                    myArtistEvents.filter(
                                        (myEvent) =>
                                            myEvent.confirmedHost &&
                                            myEvent.status === 'CONFIRMED' &&
                                            !iConfirmed(myEvent) &&
                                            new Date(myEvent.bookingWhen) >
                                                dayBeforeYesterday
                                    ).length > 0 && (
                                        <Grid
                                            container
                                            direction="column"
                                            sx={{ marginBottom: '20px' }}
                                        >
                                            <Grid item>
                                                <Typography component="h2">
                                                    {myArtistEvents.filter(
                                                        (myEvent) =>
                                                            myEvent.confirmedHost &&
                                                            myEvent.status ===
                                                                'CONFIRMED' &&
                                                            !iConfirmed(
                                                                myEvent
                                                            ) &&
                                                            new Date(
                                                                myEvent.bookingWhen
                                                            ) >
                                                                dayBeforeYesterday
                                                    ).length > 1
                                                        ? `These ${
                                                              myArtistEvents.filter(
                                                                  (myEvent) =>
                                                                      myEvent.confirmedHost &&
                                                                      myEvent.status ===
                                                                          'CONFIRMED' &&
                                                                      !iConfirmed(
                                                                          myEvent
                                                                      ) &&
                                                                      new Date(
                                                                          myEvent.bookingWhen
                                                                      ) >
                                                                          dayBeforeYesterday
                                                              ).length
                                                          } concerts were booked by another artist`
                                                        : `This concert was booked by another artist`}
                                                    :
                                                </Typography>
                                            </Grid>
                                            {/* {bookingWhenWhere && bookingWhenWhere.length > 0 && bookingWhenWhere[0].when ? //check to be sure there's a valid first entry */}
                                            <Grid
                                                container
                                                className="whenBooking"
                                                direction="row"
                                                justifyContent="center"
                                                alignItems="center"
                                                spacing={2}
                                                sx={{
                                                    margin: '0px auto 16px',
                                                    width: '100%',
                                                }}
                                            ></Grid>
                                            {myArtistEvents
                                                .filter(
                                                    (myEvent) =>
                                                        myEvent.confirmedHost &&
                                                        myEvent.status ===
                                                            'CONFIRMED' &&
                                                        !iConfirmed(myEvent) &&
                                                        new Date(
                                                            myEvent.bookingWhen
                                                        ) > dayBeforeYesterday
                                                )
                                                .map(
                                                    (thisEvent, idx) =>
                                                        thisEvent.bookingWhen &&
                                                        thisEvent.bookingWhere && (
                                                            <ArtistDashboardEventCard
                                                                key={idx}
                                                                thisEvent={
                                                                    thisEvent
                                                                }
                                                            />
                                                        )
                                                )}
                                        </Grid>
                                    )}
                                {/* Waiting for a host to offer */}
                                {myArtistEvents &&
                                    myArtistEvents.length > 0 &&
                                    myArtistEvents.filter(
                                        (event) =>
                                            event.offersFromHosts &&
                                            event.offersFromHosts.length === 0
                                    ).length > 0 && (
                                        <Grid
                                            container
                                            direction="column"
                                            sx={{
                                                margin: '8px auto',
                                            }}
                                            size={{ xs: 12 }}
                                        >
                                            <Grid item size={{ xs: 12 }}>
                                                <Typography component="h2">
                                                    {myArtistEvents.filter(
                                                        (event) =>
                                                            event.offersFromHosts &&
                                                            event
                                                                .offersFromHosts
                                                                .length === 0
                                                    ).length > 1
                                                        ? `These ${
                                                              myArtistEvents.filter(
                                                                  (event) =>
                                                                      event.offersFromHosts &&
                                                                      event
                                                                          .offersFromHosts
                                                                          .length ===
                                                                          0
                                                              ).length
                                                          } concerts are waiting for a host`
                                                        : `This concert is waiting for a host`}
                                                    :
                                                </Typography>
                                            </Grid>
                                            {/* {bookingWhenWhere && bookingWhenWhere.length > 0 && bookingWhenWhere[0].when ? //check to be sure there's a valid first entry */}
                                            <Grid
                                                container
                                                className="whenBooking"
                                                direction="row"
                                                justifyContent="center"
                                                alignItems="center"
                                                spacing={2}
                                                sx={{
                                                    margin: '0px auto 16px',
                                                    width: '100%',
                                                }}
                                                size={{ xs: 12 }}
                                            >
                                                {myArtistEvents
                                                    .filter(
                                                        (event) =>
                                                            event.offersFromHosts &&
                                                            event
                                                                .offersFromHosts
                                                                .length === 0
                                                    )
                                                    .map(
                                                        (thisEvent, idx) =>
                                                            thisEvent.bookingWhen &&
                                                            thisEvent.bookingWhere && (
                                                                <ArtistDashboardEventCard
                                                                    key={idx}
                                                                    thisEvent={
                                                                        thisEvent
                                                                    }
                                                                />
                                                            )
                                                    )}
                                            </Grid>
                                        </Grid>
                                    )}
                                <PastArtistEvents
                                    iConfirmed={iConfirmed}
                                ></PastArtistEvents>

                                <Grid item sx={{ margin: '40px auto' }}>
                                    <AddArtistEvent
                                        iConfirmed={iConfirmed}
                                    ></AddArtistEvent>
                                </Grid>
                                <CanceledArtistEvents
                                    iConfirmed={iConfirmed}
                                    recentlyCanceledIds={recentlyCanceledIds}
                                    onAnimationComplete={
                                        handleAnimateInComplete
                                    }
                                ></CanceledArtistEvents>
                            </Grid>
                        </Grid>
                    )}

                {activeHat === 'HOST' &&
                    user &&
                    user.role &&
                    user.role.indexOf('HOST') != -1 && (
                        // ((myHostEvents && myHostEvents.length > 0) ||
                        //     //(user.role.indexOf('ADMIN') > -1 ||
                        //     //user.role.indexOf('BOOKING') > -1 ||
                        //     //user.role.indexOf('TESTING') > -1) &&
                        //     (nearMeToHost && nearMeToHost.length > 0)) &&
                        <Grid
                            item
                            container
                            direction="column"
                            className="middle"
                            size={{ xs: 12, md: 6 }}
                            sx={{
                                padding: { xs: '0', md: '0 20px' },
                                width: { xs: '100%', md: '75%' },
                                maxWidth: { md: '750px' },
                            }}
                            // gap={5}
                        >
                            {
                                //DRAFTS
                                myHostEvents &&
                                    myHostEvents.length > 0 &&
                                    myHostEvents.filter(
                                        (myEvent) =>
                                            myEvent.confirmedHost &&
                                            myEvent.status === 'DRAFT' &&
                                            isFuture(myEvent.bookingWhen)
                                        // &&
                                        // new Date(myEvent.bookingWhen) <=
                                        //     dayBeforeYesterday
                                    ).length > 0 && (
                                        <Grid
                                            item
                                            container
                                            direction="column"
                                            size={{ xs: 12, md: 12 }}
                                            // sx={{ marginBottom: '40px' }}
                                            className="DRAFTs"
                                        >
                                            <Grid item size={{ xs: 12 }}>
                                                <Typography component="h2">
                                                    {myHostEvents.filter(
                                                        (myEvent) =>
                                                            myEvent.confirmedHost &&
                                                            myEvent.status ===
                                                                'DRAFT' &&
                                                            isFuture(
                                                                myEvent.bookingWhen
                                                            )
                                                    ).length > 1
                                                        ? `These ${
                                                              myHostEvents.filter(
                                                                  (myEvent) =>
                                                                      myEvent.confirmedHost &&
                                                                      myEvent.status ===
                                                                          'DRAFT' &&
                                                                      isFuture(
                                                                          myEvent.bookingWhen
                                                                      )
                                                              ).length
                                                          } offers have`
                                                        : `This offer has`}{' '}
                                                    NOT been sent yet:
                                                </Typography>
                                            </Grid>
                                            {/* {bookingWhenWhere && bookingWhenWhere.length > 0 && bookingWhenWhere[0].when ? //check to be sure there's a valid first entry */}
                                            <Grid
                                                container
                                                className="whenBooking"
                                                direction="row"
                                                justifyContent="center"
                                                alignItems="center"
                                                spacing={2}
                                                sx={{
                                                    margin: '0px auto 16px',
                                                    width: '100%',
                                                }}
                                            ></Grid>
                                            {myHostEvents
                                                .filter((e) => e) //.filter(e => e) to remove any null values
                                                .map(
                                                    (
                                                        thisEvent,
                                                        idx,
                                                        whenWhereOrig
                                                    ) =>
                                                        thisEvent.bookingWhen &&
                                                        thisEvent.bookingWhere &&
                                                        thisEvent.status ===
                                                            'DRAFT' &&
                                                        isFuture(
                                                            thisEvent.bookingWhen
                                                        ) && (
                                                            <HostDashboardEventCard
                                                                key={idx}
                                                                thisEvent={
                                                                    thisEvent
                                                                }
                                                            ></HostDashboardEventCard>
                                                        )
                                                )}
                                        </Grid>
                                    )

                                //END DRAFTS
                            }
                            {
                                // CONFIRMED AND/OR PENDING CONCERTS
                                myHostEvents &&
                                    myHostEvents.length > 0 &&
                                    // myHostEvents.filter(
                                    //     (myEvent) =>
                                    //         myEvent.confirmedHost &&
                                    //         (myEvent.status === 'CONFIRMED' ||
                                    //             myEvent.status === 'PENDING') &&
                                    //         isFuture(myEvent.bookingWhen)
                                    //     // &&
                                    //     // new Date(myEvent.bookingWhen) <=
                                    //     //     dayBeforeYesterday
                                    // ).length > 0 && (
                                    myHostEvents.filter(shouldShowHostEvent)
                                        .length > 0 && (
                                        <Grid
                                            item
                                            container
                                            direction="column"
                                            size={{ xs: 12, md: 12 }}
                                        >
                                            <Grid item size={{ xs: 12 }}>
                                                <Typography component="h2">
                                                    You have offered to host{' '}
                                                    {/* {myHostEvents.filter(
                                                        (myEvent) =>
                                                            myEvent.confirmedHost &&
                                                            (myEvent.status ===
                                                                'CONFIRMED' ||
                                                                myEvent.status ===
                                                                    'PENDING') &&
                                                            isFuture(
                                                                myEvent.bookingWhen
                                                            )
                                                        // new Date(
                                                        //     myEvent.bookingWhen
                                                        // ) <=
                                                        //     dayBeforeYesterday
                                                    ).length > 1 */}
                                                    {myHostEvents.filter(
                                                        shouldShowHostEvent
                                                    ).length > 1
                                                        ? `these ${
                                                              // myHostEvents.filter(
                                                              //     (myEvent) =>
                                                              //         myEvent.confirmedHost &&
                                                              //         (myEvent.status ===
                                                              //             'CONFIRMED' ||
                                                              //             myEvent.status ===
                                                              //                 'PENDING') &&
                                                              //         isFuture(
                                                              //             myEvent.bookingWhen
                                                              //         )
                                                              //     //   new Date(
                                                              //     //       myEvent.bookingWhen
                                                              //     //   ) <=
                                                              //     //       dayBeforeYesterday
                                                              // ).length
                                                              myHostEvents.filter(
                                                                  shouldShowHostEvent
                                                              ).length
                                                          } concerts`
                                                        : `this concert`}
                                                    :
                                                </Typography>
                                            </Grid>
                                            {/* {bookingWhenWhere && bookingWhenWhere.length > 0 && bookingWhenWhere[0].when ? //check to be sure there's a valid first entry */}
                                            <Grid
                                                container
                                                className="whenBooking"
                                                direction="row"
                                                justifyContent="center"
                                                alignItems="center"
                                                spacing={2}
                                                sx={{
                                                    margin: '0px auto 16px',
                                                    width: '100%',
                                                }}
                                            ></Grid>
                                            {myHostEvents
                                                .filter((e) => e) //.filter(e => e) to remove any null values
                                                .map(
                                                    (
                                                        thisEvent,
                                                        idx,
                                                        whenWhereOrig
                                                    ) =>
                                                        // thisEvent.bookingWhen &&
                                                        // thisEvent.bookingWhere &&
                                                        // (thisEvent.status ===
                                                        //     'CONFIRMED' ||
                                                        //     thisEvent.status ===
                                                        //         'PENDING') &&
                                                        // // new Date(
                                                        // //     thisEvent.bookingWhen
                                                        // // ) >
                                                        // //     dayBeforeYesterday
                                                        // isFuture(
                                                        //     thisEvent.bookingWhen
                                                        // ) && (
                                                        shouldShowHostEvent(
                                                            thisEvent
                                                        ) && (
                                                            <Collapse
                                                                key={
                                                                    (thisEvent &&
                                                                        thisEvent._id) ||
                                                                    idx
                                                                }
                                                                in={
                                                                    !recentlyCanceledIds.includes(
                                                                        thisEvent._id
                                                                    )
                                                                }
                                                                timeout={{
                                                                    enter: 300,
                                                                    exit: 350,
                                                                }}
                                                                unmountOnExit
                                                                sx={{
                                                                    width: '100%',
                                                                }}
                                                            >
                                                                <AnimatedEventCardSlot
                                                                    eventId={
                                                                        thisEvent._id
                                                                    }
                                                                    isArriving={recentlyOfferedIds.includes(
                                                                        thisEvent._id
                                                                    )}
                                                                    onAnimationComplete={
                                                                        handleAnimateInComplete
                                                                    }
                                                                >
                                                                    <HostDashboardEventCard
                                                                        thisEvent={
                                                                            thisEvent
                                                                        }
                                                                        onCancelStarted={
                                                                            handleCancelStarted
                                                                        }
                                                                    ></HostDashboardEventCard>
                                                                </AnimatedEventCardSlot>
                                                            </Collapse>
                                                        )
                                                )}
                                        </Grid>
                                    )
                                //END CONFIRMED CONCERTS
                            }
                            {/* End myHostEvents */}
                            {Array.isArray(user.role) &&
                                user.role.indexOf('HOST') != -1 &&
                                host.me &&
                                host.me.adminActive && (
                                    <Grid item sx={{ margin: '40px auto' }}>
                                        <AddHostEvent></AddHostEvent>
                                    </Grid>
                                )}
                            {host && host.me && host.me.adminActive != true && (
                                <Grid
                                    className="adminActiveFalse"
                                    container
                                    sx={{ marginBottom: '16px' }}
                                >
                                    <HostAdminActiveFalse></HostAdminActiveFalse>
                                </Grid>
                            )}
                            {
                                //(user.role.indexOf('ADMIN') > -1 ||
                                //user.role.indexOf('BOOKING') > -1 ||
                                //user.role.indexOf('TESTING') > -1) &&
                                nearMeToHost && nearMeToHost.length > 0 && (
                                    <Grid
                                        item
                                        container
                                        direction="column"
                                        size={{ xs: 12, md: 12 }}
                                    >
                                        <Grid
                                            item
                                            container
                                            size={{ xs: 12 }}
                                            alignItems="center"
                                            justifyContent="space-between"
                                        >
                                            <Typography component="h2">
                                                {nearMeToHostFiltered.length > 1
                                                    ? `There are these ${nearMeToHostFiltered.length} potential events `
                                                    : nearMeToHostFiltered.length ===
                                                        0
                                                      ? `There aren’t any other events `
                                                      : `There is this ${nearMeToHostFiltered.length} potential event `}
                                                looking to be hosted in your
                                                area:
                                            </Typography>
                                            <Tooltip
                                                arrow
                                                placement="left"
                                                title={
                                                    hideDeclinedNearMe
                                                        ? 'Show declined events'
                                                        : 'Hide declined events'
                                                }
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        setHideDeclinedNearMe(
                                                            (v) => !v
                                                        )
                                                    }
                                                    sx={{
                                                        color: 'var(--primary-color)',
                                                    }}
                                                >
                                                    {hideDeclinedNearMe ? (
                                                        <VisibilityOffIcon />
                                                    ) : (
                                                        <VisibilityIcon />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        </Grid>
                                        {/* {bookingWhenWhere && bookingWhenWhere.length > 0 && bookingWhenWhere[0].when ? //check to be sure there's a valid first entry */}
                                        <Grid
                                            container
                                            className="whenBooking"
                                            direction="row"
                                            justifyContent="center"
                                            alignItems="center"
                                            spacing={2}
                                            sx={{
                                                margin: '0px auto 16px',
                                                width: '100%',
                                            }}
                                        ></Grid>
                                        {(Array.isArray(nearMeToHost)
                                            ? nearMeToHost
                                            : []
                                        )
                                            .filter((e) => e) //.filter(e => e) to remove any null values
                                            .map(
                                                (
                                                    thisEvent,
                                                    idx,
                                                    whenWhereOrig
                                                ) => {
                                                    const declinedAndHidden =
                                                        hideDeclinedNearMe &&
                                                        isDeclinedByCurrentHost(
                                                            thisEvent
                                                        );
                                                    const justOffered =
                                                        recentlyOfferedIds.includes(
                                                            thisEvent._id
                                                        );
                                                    const hidden =
                                                        declinedAndHidden ||
                                                        justOffered;
                                                    return (
                                                        <Collapse
                                                            in={!hidden}
                                                            key={
                                                                (thisEvent &&
                                                                    thisEvent._id) ||
                                                                idx
                                                            }
                                                            timeout={{
                                                                enter: 250,
                                                                exit: 300,
                                                            }}
                                                            unmountOnExit
                                                            sx={{
                                                                width: '100%',
                                                            }}
                                                        >
                                                            {thisEvent.bookingWhen &&
                                                                thisEvent.bookingWhere && (
                                                                    <NearMeToHostEventCard
                                                                        thisEvent={
                                                                            thisEvent
                                                                        }
                                                                        idx={
                                                                            idx
                                                                        }
                                                                        key={
                                                                            (thisEvent &&
                                                                                thisEvent._id) ||
                                                                            idx
                                                                        }
                                                                        onHostOfferStarted={
                                                                            handleHostOfferStarted
                                                                        }
                                                                    ></NearMeToHostEventCard>
                                                                )}
                                                        </Collapse>
                                                    );
                                                }
                                            )}
                                    </Grid>
                                )
                            }
                            {/* End .nearMeToHost */}
                            <PastHostEvents
                                iConfirmed={iConfirmed}
                            ></PastHostEvents>
                            <CanceledHostEvents
                                iConfirmed={iConfirmed}
                                recentlyCanceledIds={recentlyCanceledIds}
                                onAnimationComplete={handleAnimateInComplete}
                            ></CanceledHostEvents>
                        </Grid>
                    )}
            </Grid>
        </Fragment>
    );
};

Dashboard.propTypes = {
    //getCurrentProfile: PropTypes.func.isRequired,
    getCurrentArist: PropTypes.func,
    getCurrentHost: PropTypes.func,
    getCurrentAttendee: PropTypes.func,
    getDiscoverableEvents: PropTypes.func,
    updateEventDiscoveryRadius: PropTypes.func,
    deleteAccount: PropTypes.func.isRequired,
    auth: PropTypes.object.isRequired,
    //profile: PropTypes.object.isRequired,
    artist: PropTypes.object.isRequired,
    app: PropTypes.object.isRequired,
    host: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired,
    attendee: PropTypes.object,
};

const mapStateToProps = (state) => ({
    auth: state.auth,
    //profile: state.profile,
    artist: state.artist,
    host: state.host,
    event: state.event,
    app: state.app,
    attendee: state.attendee,
});

export default connect(mapStateToProps, {
    //getCurrentProfile,
    changeHats,
    getCurrentArtist,
    getCurrentHost,
    getCurrentAttendee,
    getDiscoverableEvents,
    updateEventDiscoveryRadius,
    deleteAccount,
    getEventsNearMeToHost,
    getMyArtistEventsOffers,
    closeEventEditDrawer,
})(Dashboard);
